import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { COUNTRIES, COUNTRY_CALLING_CODES } from '../utils/countries';

// Add this with your other imports at the top of LenderBorrowerApply.js


const TextMotion = styled(motion.span)(({ theme }) => ({
    display: 'inline-block',
    fontWeight: 700,
    // Link-style underline so the action reads as clickable
    textDecoration: 'underline',
    textUnderlineOffset: '5px',
    textDecorationThickness: '2px'
}));

const WordSpacer = styled('span')(({ theme }) => ({
    display: 'inline-block',
    width: '10px',
    [theme.breakpoints.down('sm')]: {
        width: '8px'
    }
}));

const IconMotion = styled(motion.div)({
    display: 'inline-flex',
    alignItems: 'center'
});

const RetroArrow = styled('span')(({ glowColor }) => ({
    fontFamily: "'Press Start 2P', cursive",
    fontSize: '28px',
    '@media (max-width: 600px)': {
        fontSize: '20px',
    },
    color: '#FFFFFF',
    textShadow: `
        0 0 6px #FFFFFF,
        0 0 12px ${glowColor}
    `,
    lineHeight: 1
}));

const RetroTextField = styled(motion.input)(({ theme }) => ({
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: '2px solid #FFFFFF',
    borderRadius: '4px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontFamily: "'Press Start 2P', cursive",
    '@media (max-width: 600px)': {
        fontSize: '12px',
        padding: '11px 10px',
    },
    boxSizing: 'border-box', // Ensure borders are included in the width
    '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.7)',
    },
    outline: 'none',
    marginBottom: '15px',
    transition: 'all 0.3s ease',
    '&:focus': {
        borderColor: '#00BFFF',
        boxShadow: `
            0 0 10px #FFFFFF,
            0 0 20px #00BFFF,
            0 0 30px #00BFFF
        `,
    },
    // Remove number input spinners
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
    },
    '&[type=number]': {
        '-moz-appearance': 'textfield',
    }
}));

const RetroFieldLabel = styled(Typography)({
    fontFamily: "'Press Start 2P', cursive",
    fontSize: '14px',
    color: '#FFFFFF',
    marginBottom: '10px',
    textAlign: 'center'
});

const RetroRadioGroup = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
});

const RetroChoicesContainer = styled(Box)({
    display: 'flex',
    gap: '4px',
    justifyContent: 'center',
});

const RetroRadioButton = styled(motion.button)(({ selected }) => {
    // Retro neon sign: a bright glowing tube outline with a TIGHT halo and
    // crisp text — no wide diffuse bloom, no solid filled blob
    const activeGlow = {
        backgroundColor: 'rgba(204, 102, 255, 0.26)',
        borderColor: '#f0bcff',
        textShadow: `
            0 0 2px #ffffff,
            0 0 7px #cc66ff,
            0 0 12px #cc66ff
        `,
        boxShadow: `
            0 0 3px #cc66ff,
            0 0 11px #cc66ff,
            inset 0 0 8px rgba(204, 102, 255, 0.75)
        `
    };

    return {
        backgroundColor: selected ? 'rgba(204, 102, 255, 0.26)' : 'transparent',
        border: `2px solid ${selected ? '#f0bcff' : '#FFFFFF'}`,
        padding: '8px 16px',
        color: '#FFFFFF',
        fontSize: '14px',
        fontFamily: "'Press Start 2P', cursive",
        cursor: 'pointer',
        position: 'relative',
        textShadow: selected ? activeGlow.textShadow : 'none',
        boxShadow: selected ? activeGlow.boxShadow : 'none',
        outline: 'none',
        transition: 'background-color 0.12s ease-out, border-color 0.12s ease-out, box-shadow 0.12s ease-out, color 0.12s ease-out',
        '&:first-of-type': {
            borderTopLeftRadius: '4px',
            borderBottomLeftRadius: '4px',
            borderRight: selected ? '2px solid #f0bcff' : '2px solid #FFFFFF',
            marginRight: '-2px', // Compensate for the double border
        },
        '&:last-of-type': {
            borderTopRightRadius: '4px',
            borderBottomRightRadius: '4px',
        },
        '&:hover': {
            ...(selected
                ? activeGlow
                : {
                    backgroundColor: 'rgba(204, 102, 255, 0.18)',
                    borderColor: '#FFFFFF',
                    textShadow: `0 0 4px rgba(204, 102, 255, 0.7)`,
                    boxShadow: `0 0 6px rgba(204, 102, 255, 0.5)`
                }),
        },
        '&:focus-visible, &:focus, &:active': {
            ...activeGlow,
        }
    };
});


const RetroTextArea = styled(motion.textarea)(({ theme }) => ({
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: '2px solid #FFFFFF',
    borderRadius: '4px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontFamily: "'Press Start 2P', cursive",
    '@media (max-width: 600px)': {
        fontSize: '12px',
        padding: '11px 10px',
    },
    resize: 'none',
    minHeight: '100px',
    '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.7)',
    },
    outline: 'none',
    marginBottom: '15px',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    '&:focus': {
        borderColor: '#00BFFF',
        boxShadow: `
            0 0 10px #FFFFFF,
            0 0 20px #00BFFF,
            0 0 30px #00BFFF
        `
    }
}));

const SubmitButton = ({ mode, isClicked, onClick, formStep }) => {
    const getTextParts = () => {
        switch (formStep) {
            case 1:
                return mode === 'borrow'
                    ? { first: 'FIND', second: 'LENDERS' }
                    : { first: 'FIND', second: 'BORROWERS' };
            case 2:
                return { first: 'CONTINUE', second: '' };
            case 3:
                return { first: 'SUBMIT', second: '' }; // Only one word for final step
            default:
                return { first: '', second: '' };
        }
    };

    const textParts = getTextParts();
    const glowColor = mode === 'lend' ? '#00FF41' : '#00BFFF';
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <motion.button
            onClick={onClick}
            style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                // Arrow sits right after the text, one line, like a link
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '8px' : '12px',
                padding: isMobile ? '4px 8px' : '10px 16px',
                margin: 0,
                color: '#FFFFFF',
                fontSize: isMobile ? '15px' : '24px',
                fontFamily: "'Press Start 2P', cursive",
                fontWeight: 700,
                textAlign: 'center',
                textShadow: `
                    0 0 6px #FFFFFF,
                    0 0 12px ${glowColor}
                `,
                flexWrap: 'nowrap'
            }}
            whileHover={{
                rotateX: 360,
                transition: {
                    duration: 1.2,
                    ease: "easeInOut"
                }
            }}
            whileTap={{ scale: 0.98 }}
        >
            <TextMotion
                animate={isClicked ? {
                    x: -1000,
                    opacity: 0,
                    transition: {
                        duration: 0.5,
                        ease: [0.2, 0.65, 0.3, 0.9]
                    }
                } : {
                    x: 0,
                    opacity: 1
                }}
            >
                {textParts.first}
            </TextMotion>

            {textParts.second && (
                <>
                    <WordSpacer />
                    <TextMotion
                        animate={isClicked ? {
                            x: 1000,
                            opacity: 0,
                            transition: {
                                duration: 0.5,
                                ease: [0.2, 0.65, 0.3, 0.9]
                            }
                        } : {
                            x: 0,
                            opacity: 1
                        }}
                    >
                        {textParts.second}
                    </TextMotion>
                </>
            )}

            <IconMotion
                animate={isClicked ? {
                    x: 1000,
                    scale: 2,
                    opacity: 0,
                    transition: {
                        duration: 0.5,
                        scale: {
                            duration: 0.3,
                            ease: "easeOut"
                        },
                        ease: [0.2, 0.65, 0.3, 0.9]
                    }
                } : {
                    x: 0,
                    scale: 1,
                    opacity: 1
                }}
            >
                <RetroArrow glowColor={glowColor}>
                    ▶
                </RetroArrow>
            </IconMotion>
        </motion.button>
    );
};

const ToggleBar = styled(Box)(({ theme }) => ({
    width: '90%',
    maxWidth: '1200px',
    height: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    position: 'relative',
    margin: '20px auto 40px',
    borderRadius: '7px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '@media (max-width: 600px)': {
        width: '100%',
        maxWidth: '300px',
    },
}));

const GlowingSlider = styled(motion.div)(({ glowColor }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '50%',
    borderRadius: 'inherit',
    backgroundColor: glowColor,
    boxShadow: `0 0 10px ${glowColor}`,
    opacity: 0.95,
    filter: 'saturate(120%) brightness(1.05)',
    cursor: 'pointer',
    transition: 'box-shadow 0.3s ease, opacity 0.3s ease'
}));

// Sized to match ToggleBar exactly so each word (50% wide, centered) sits
// directly over its half of the glow bar below.
const ToggleContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '90%',
    maxWidth: '1200px',
    margin: '0 auto',
    '@media (max-width: 600px)': {
        width: '100%',
        maxWidth: '300px',
    },
}));

const ToggleText = styled(Typography)(({ active, glowColor }) => ({
    fontFamily: "'Press Start 2P', cursive",
    fontSize: '28px',
    '@media (max-width: 600px)': {
        fontSize: '19px',
    },
    flex: '0 0 50%',
    textAlign: 'center',
    color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textShadow: active ? `
        0 0 4px #FFFFFF,
        0 0 10px ${glowColor}
    ` : 'none',
    '&:hover': {
        color: '#FFFFFF',
        textShadow: `
            0 0 4px #FFFFFF,
            0 0 10px ${glowColor}
        `
    }
}));

const LenderBorrowerApply = () => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
    const [mode, setMode] = useState('borrow');
    const [isClicked, setIsClicked] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        amount: '',
        purpose: '',
        country: ''
    });
    const [displayAmount, setDisplayAmount] = useState('');

    // Country autocomplete: matches appear after the first typed letter,
    // prefix matches ranked before contains matches
    const countrySuggestions = (() => {
        const q = (formData.country || '').trim().toLowerCase();
        if (!q) return [];
        const starts = COUNTRIES.filter((c) => c.toLowerCase().startsWith(q));
        const contains = COUNTRIES.filter(
            (c) => !c.toLowerCase().startsWith(q) && c.toLowerCase().includes(q)
        );
        const matches = [...starts, ...contains].slice(0, 10);
        // Nothing to suggest once the field exactly equals a country
        if (matches.length === 1 && matches[0].toLowerCase() === q) return [];
        return matches;
    })();

    const [formStep, setFormStep] = useState(1);
    const [extendedFormData, setExtendedFormData] = useState({
        name: '',
        company: '',
        cryptoCollateral: '',
        lendingExperience: '',
        fiatCollateral: ''
    });

    const [finalFormData, setFinalFormData] = useState({
        hasProperty: '',
        otherAssets: '',
        address: '',
        phone: '',
        description: ''
    });

    const [pulsedField, setPulsedField] = useState('');
    const [stepError, setStepError] = useState('');
    const stepErrorTimer = useRef(null);

    // FIND / FINISH / SUBMIT button sound — the same two-tone coin-drop
    // beep the God Mode toggle plays (square waves at A5 then E6).
    const playRetroShot = () => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') ctx.resume();
            const playBeep = (freq, startOffset) => {
                const start = ctx.currentTime + startOffset;
                const dur = 0.09;
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'square';
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0, start);
                gain.gain.linearRampToValueAtTime(0.18, start + 0.005);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
                osc.connect(gain).connect(ctx.destination);
                osc.start(start);
                osc.stop(start + dur);
            };
            playBeep(880, 0);     // A5
            playBeep(1320, 0.1);  // E6 — coin-drop two-tone
        } catch (e) {
            // Sound is best-effort; never block the form on audio errors.
        }
    };

    // Computer-generated swipe sound for the Borrow/Lend toggle: a short
    // band-passed noise sweep synthesized with the Web Audio API (no audio
    // file). Sweeps up when switching to Lend, down when back to Borrow.
    const audioCtxRef = useRef(null);

    const playSwipeSound = (direction) => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') ctx.resume();

            const dur = 0.28;
            const bufferSize = Math.floor(ctx.sampleRate * dur);
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

            const source = ctx.createBufferSource();
            source.buffer = buffer;

            const filter = ctx.createBiquadFilter();
            filter.type = 'bandpass';
            filter.Q.value = 1.2;
            const now = ctx.currentTime;
            // Same swipe both ways: the downward sweep (the "borrow" sound).
            filter.frequency.setValueAtTime(3200, now);
            filter.frequency.exponentialRampToValueAtTime(400, now + dur);

            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.exponentialRampToValueAtTime(0.5, now + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

            source.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);
            source.start(now);
            source.stop(now + dur);
        } catch (e) {
            // Sound is best-effort; never block the toggle on audio errors.
        }
    };

    const switchMode = (next) => {
        if (next !== mode) playSwipeSound(next);
        setMode(next);
    };

    const handleToggleBarClick = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        switchMode(clickX <= rect.width / 2 ? 'borrow' : 'lend');
    };

    const handleSliderClick = (event) => {
        event.stopPropagation();
        switchMode(mode === 'borrow' ? 'lend' : 'borrow');
    };

    const handleToggleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            switchMode(mode === 'borrow' ? 'lend' : 'borrow');
        }
    };

    const handleBlur = (field, value) => {
        setPulsedField(field);
        setTimeout(() => setPulsedField(''), 600);
    };


    // Live money formatting: typing 5000000 renders $5,000,000 immediately
    // (one optional decimal point, max two decimal digits)
    const formatUsdInput = (input) => {
        const cleaned = String(input).replace(/[^0-9.]/g, '');
        if (cleaned === '') {
            return { raw: '', display: '' };
        }
        const [intPart, ...decParts] = cleaned.split('.');
        const hasDot = cleaned.includes('.');
        const dec = decParts.join('').slice(0, 2);
        const intFmt = intPart ? Number(intPart).toLocaleString('en-US') : '0';
        return {
            raw: (intPart || '0') + (hasDot ? '.' + dec : ''),
            display: '$' + intFmt + (hasDot ? '.' + dec : '')
        };
    };

    // Handle amount input change - format live as the user types
    const handleAmountChange = (e) => {
        const { raw, display } = formatUsdInput(e.target.value);
        setFormData({ ...formData, amount: raw });
        setDisplayAmount(display);
    };

    const handleAmountBlur = () => {
        handleBlur('amount', formData.amount);
    };

    // Kept for the input's onFocus: the field stays formatted while editing
    const handleAmountFocus = () => {};

    // Format phone number as +1 (XXX) XXX-XXXX
    // International phone formatting. The digits INCLUDE the country code:
    // +1 gets the familiar +1 (XXX) XXX-XXXX; any other country code is
    // shown as +<digits> in groups of three (E.164 max 15 digits).
    const formatIntlPhone = (digits) => {
        if (!digits) {
            return '';
        }
        if (digits[0] === '1') {
            const rest = digits.slice(1, 11);
            if (rest.length === 0) return '+1 ';
            if (rest.length <= 3) return `+1 (${rest}`;
            if (rest.length <= 6) return `+1 (${rest.slice(0, 3)}) ${rest.slice(3)}`;
            return `+1 (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
        }
        // Country calling codes are 1-3 digits; the length is determined by
        // the leading digits (ITU E.164 zones). Split the code off correctly
        // so +44 (UK) doesn't swallow the first national digit while +255
        // (Tanzania) keeps all three.
        const ccLen = (() => {
            const a = digits[0], b = digits[1] || '';
            if (a === '7') return 1;
            if (a === '2') return (b === '0' || b === '7') ? 2 : 3;
            if (a === '3') return '0123469'.includes(b) ? 2 : 3;
            if (a === '4') return b === '2' ? 3 : 2;
            if (a === '5') return (b === '0' || b === '9') ? 3 : 2;
            if (a === '6') return '0123456'.includes(b) ? 2 : 3;
            if (a === '8') return '1246'.includes(b) ? 2 : 3;
            if (a === '9') return '0123458'.includes(b) ? 2 : 3;
            return 2;
        })();
        const cc = digits.slice(0, ccLen);
        const rest = digits.slice(ccLen);
        if (!rest) return '+' + cc + (digits.length >= ccLen ? ' ' : '');
        return '+' + cc + ' ' + (rest.match(/\d{1,3}/g) || []).join(' ');
    };

    // Handle phone number input
    const handlePhoneChange = (e) => {
        const input = e.target.value;
        let digits = input.replace(/\D/g, '').slice(0, 15);
        const prev = finalFormData.phone || '';
        // Backspacing a formatting character (space, dash, paren) leaves the
        // digits unchanged, which used to make delete appear dead — treat it
        // as deleting the last digit instead
        if (digits === prev.replace(/\D/g, '') && input.length < prev.length) {
            digits = digits.slice(0, -1);
        }
        setFinalFormData({ ...finalFormData, phone: formatIntlPhone(digits) });
    };

    // Pre-fill the country calling code from the country chosen on step 1
    // (falling back to +1); the user can still erase and type any code
    const handlePhoneFocus = () => {
        if (!finalFormData.phone) {
            const chosen = (formData.country || '').trim().toLowerCase();
            const match = Object.keys(COUNTRY_CALLING_CODES).find(
                (name) => name.toLowerCase() === chosen
            );
            const code = match ? COUNTRY_CALLING_CODES[match] : '1';
            setFinalFormData({ ...finalFormData, phone: formatIntlPhone(code) });
        }
    };

    // Add this helper function
    const handleRadioSelect = (field, value) => {
        playRetroShot();
        setExtendedFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Step-3 yes/no selects get the same coin beep
    const handleFinalRadioSelect = (field, value) => {
        playRetroShot();
        setFinalFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isClicked) {
            setIsClicked(true);

            // Wait for animation to complete before submitting
            setTimeout(async () => {
                try {
                    const response = await fetch('/api/apply', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            ...formData,
                            type: mode
                        }),
                    });

                    if (!response.ok) throw new Error('Failed to submit application');
                    setFormData({ email: '', amount: '', purpose: '', country: '' });
                    alert('Application submitted successfully!');
                } catch (error) {
                    console.error('Error:', error);
                    alert('Failed to submit application. Please try again.');
                }
                setIsClicked(false);
            }, 500); // Wait for shoot animation to complete
        }
    };

    // Every field on the current slide is required; returns the first
    // problem as { field, message } or null when the slide is complete
    const validateStep = () => {
        const empty = (v) => !String(v || '').trim();
        if (formStep === 1) {
            if (empty(formData.email)) return { field: 'email', message: 'EMAIL IS REQUIRED' };
            if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) return { field: 'email', message: 'ENTER A VALID EMAIL' };
            if (empty(formData.amount)) return { field: 'amount', message: mode === 'borrow' ? 'LOAN AMOUNT IS REQUIRED' : 'INVESTMENT AMOUNT IS REQUIRED' };
            if (empty(formData.purpose)) return { field: 'purpose', message: mode === 'borrow' ? 'LOAN PURPOSE IS REQUIRED' : 'INVESTMENT STRATEGY IS REQUIRED' };
            if (empty(formData.country)) return { field: 'country', message: 'COUNTRY IS REQUIRED' };
        } else if (formStep === 2) {
            if (empty(extendedFormData.name)) return { field: 'name', message: 'NAME IS REQUIRED' };
            if (empty(extendedFormData.company)) return { field: 'company', message: 'COMPANY IS REQUIRED' };
            if (empty(extendedFormData.cryptoCollateral)) return { field: '', message: 'SELECT CRYPTO COLLATERAL' };
            if (mode === 'lend') {
                if (empty(extendedFormData.lendingExperience)) return { field: 'lendingExperience', message: 'LENDING EXPERIENCE IS REQUIRED' };
            } else if (empty(extendedFormData.annualIncome)) {
                return { field: 'annualIncome', message: 'ANNUAL INCOME IS REQUIRED' };
            }
            if (empty(extendedFormData.fiatCollateral)) return { field: '', message: 'SELECT FIAT COLLATERAL' };
        } else {
            if (empty(finalFormData.hasProperty)) return { field: '', message: mode === 'borrow' ? 'SELECT OWN PROPERTY' : 'SELECT REAL ESTATE' };
            if (empty(finalFormData.otherAssets)) return { field: '', message: 'SELECT OTHER ASSETS' };
            if (empty(finalFormData.address)) return { field: 'address', message: 'ADDRESS IS REQUIRED' };
            if (String(finalFormData.phone || '').replace(/\D/g, '').length < 7) return { field: 'phone', message: 'PHONE NUMBER IS REQUIRED' };
            if (empty(finalFormData.description)) return { field: 'description', message: 'DESCRIPTION IS REQUIRED' };
        }
        return null;
    };

    const showStepError = (invalid) => {
        setStepError(invalid.message);
        // Pulse the offending text field (radio groups have no pulse class)
        if (invalid.field) {
            setPulsedField(invalid.field);
            setTimeout(() => setPulsedField(''), 900);
        }
        if (stepErrorTimer.current) clearTimeout(stepErrorTimer.current);
        stepErrorTimer.current = setTimeout(() => setStepError(''), 3000);
    };

    const handleStepSubmit = () => {
        if (formStep === 1) {
            setIsClicked(true);
            setTimeout(() => {
                setFormStep(2);
                setIsClicked(false);
            }, 800);
        } else if (formStep === 2) {
            setIsClicked(true);
            setTimeout(() => {
                setFormStep(3);
                setIsClicked(false);
            }, 800);
        } else {
            handleFinalSubmit();
        }
    };

    const handleFinalSubmit = async () => {
        setIsClicked(true);

        console.log('Starting form submission...', {
            email: formData.email,
            mode: mode
        });

        try {
            // Call signup endpoint with email and application data
            const response = await axios.post('/api/signup', {
                email: formData.email,
                applicationData: {
                    ...formData,
                    ...extendedFormData,
                    ...finalFormData,
                    type: mode
                }
            });

            console.log('Signup response received:', response.status, response.data);

            if (response.status === 201 || response.status === 200) {
                console.log('Setting isSubmitted to true');
                setIsSubmitted(true);  // This will trigger your existing success message
            } else {
                console.error('Unexpected response status:', response.status);
                alert('Unexpected response from server. Please try again.');
                setIsClicked(false);
            }
        } catch (error) {
            console.error('Submission error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                stack: error.stack
            });
            alert(error.response?.data?.error || 'Failed to submit application. Please try again.');
            setIsClicked(false);
        }
    };




    const renderThirdStep = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {!isSubmitted ? (
                <>
                    {/* Own Property Selection */}
                    <motion.div
                        initial={{ x: '100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '-100%', opacity: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <RetroRadioGroup>
                            <RetroFieldLabel>
                                {mode === 'borrow' ? 'OWN PROPERTY' : 'REAL ESTATE'}
                            </RetroFieldLabel>
                            <RetroChoicesContainer>
                                <RetroRadioButton
                                    aria-pressed={finalFormData.hasProperty === 'yes'}
                                    selected={finalFormData.hasProperty === 'yes'}
                                    onClick={() => handleFinalRadioSelect('hasProperty', 'yes')}
                                    type="button"
                                >
                                    YES
                                </RetroRadioButton>
                                <RetroRadioButton
                                    aria-pressed={finalFormData.hasProperty === 'no'}
                                    selected={finalFormData.hasProperty === 'no'}
                                    onClick={() => handleFinalRadioSelect('hasProperty', 'no')}
                                    type="button"
                                >
                                    NO
                                </RetroRadioButton>
                            </RetroChoicesContainer>
                        </RetroRadioGroup>
                    </motion.div>

                    {/* Other Assets Selection */}
                    <motion.div
                        initial={{ x: '-100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '100%', opacity: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        <RetroRadioGroup>
                            <RetroFieldLabel>
                                OTHER ASSETS
                            </RetroFieldLabel>
                            <RetroChoicesContainer>
                                <RetroRadioButton
                                    aria-pressed={finalFormData.otherAssets === 'yes'}
                                    selected={finalFormData.otherAssets === 'yes'}
                                    onClick={() => handleFinalRadioSelect('otherAssets', 'yes')}
                                    type="button"
                                >
                                    YES
                                </RetroRadioButton>
                                <RetroRadioButton
                                    aria-pressed={finalFormData.otherAssets === 'no'}
                                    selected={finalFormData.otherAssets === 'no'}
                                    onClick={() => handleFinalRadioSelect('otherAssets', 'no')}
                                    type="button"
                                >
                                    NO
                                </RetroRadioButton>
                            </RetroChoicesContainer>
                        </RetroRadioGroup>
                    </motion.div>

                    {/* Address Input */}
                    <motion.div
                        initial={{ x: '100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '-100%', opacity: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <RetroTextField
                            type="text"
                            placeholder="Address"
                            value={finalFormData.address}
                            onChange={(e) => setFinalFormData({ ...finalFormData, address: e.target.value })}
                            onBlur={() => handleBlur('address', finalFormData.address)}
                            className={pulsedField === 'address' ? 'field-pulse' : ''}
                            required
                        />
                    </motion.div>

                    {/* Phone Input */}
                    <motion.div
                        initial={{ x: '-100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '100%', opacity: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <RetroTextField
                            type="tel"
                            placeholder="+1 (XXX) XXX-XXXX"
                            value={finalFormData.phone}
                            onChange={handlePhoneChange}
                            onFocus={handlePhoneFocus}
                            onBlur={() => handleBlur('phone', finalFormData.phone)}
                            className={pulsedField === 'phone' ? 'field-pulse' : ''}
                            required
                        />
                    </motion.div>

                    {/* Detailed Description Input */}
                    <motion.div
                        initial={{ x: '100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '-100%', opacity: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        <RetroTextArea
                            placeholder="Detailed Description"
                            value={finalFormData.description}
                            onChange={(e) => {
                                if (e.target.value.length <= 120) {
                                    setFinalFormData({ ...finalFormData, description: e.target.value });
                                }
                            }}
                            onBlur={() => handleBlur('description', finalFormData.description)}
                            className={pulsedField === 'description' ? 'field-pulse' : ''}
                            maxLength={120}
                            required
                        />
                    </motion.div>

                    {/* Sparkling Flash Animation */}
                    {isClicked && (
                        <motion.div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(to right, transparent, #FFFFFF, transparent)',
                                opacity: 0,
                                zIndex: 10
                            }}
                            animate={{
                                opacity: [0, 1, 0],
                                x: ['-100%', '100%'],
                            }}
                            transition={{ duration: 0.8 }}
                        />
                    )}
                </>
            ) : (
                // Success Message
                <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                        duration: 0.5,
                        delay: 0.3,
                        ease: "easeOut",
                    }}
                    style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "'Press Start 2P', cursive",
                            fontSize: '20px',
                            color: '#FFFFFF',
                            textShadow: `
                                0 0 10px #FFFFFF,
                                0 0 20px #00BFFF,
                                0 0 30px #00BFFF
                            `,
                            lineHeight: '1.5',
                            marginBottom: '20px',
                        }}
                    >
                        🎉 Success! Check your email to verify your account 🎉
                    </Typography>

                    <Typography
                        sx={{
                            color: '#FFFFFF',
                            fontSize: '14px',
                            lineHeight: '1.8',
                            marginBottom: '30px',
                            opacity: 0.9,
                        }}
                    >
                        Once verified, visit your dashboard to:
                        <br />
                        • Upload your profile picture
                        <br />
                        • Add detailed pitch (text/document/video)
                        <br />
                        • Track your application status
                    </Typography>

                    <motion.a
                        href="https://loans.satoshiadvance.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        style={{
                            // Blocky raised retro button: solid panel, square
                            // corners, hard offset shadow instead of soft glow
                            display: 'inline-block',
                            padding: '16px 24px',
                            backgroundColor: '#1a237e',
                            border: '3px solid #FFFFFF',
                            color: '#FFFFFF',
                            fontFamily: "'Press Start 2P', cursive",
                            fontSize: '12px',
                            lineHeight: '1.6',
                            textDecoration: 'none',
                            borderRadius: 0,
                            textShadow: '0 0 6px #FFFFFF, 0 0 12px #00BFFF',
                            boxShadow: '5px 5px 0 #00BFFF',
                        }}
                    >
                        Check Options in Lending Dashboard{' '}
                        <span style={{
                            display: 'inline-block',
                            fontSize: '13px',
                            textShadow: '0 0 6px #FFFFFF, 0 0 12px #00BFFF'
                        }}>
                            ▶
                        </span>
                    </motion.a>
                </motion.div>
            )}
        </Box>
    );


    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '1200px' },
                mx: 'auto',
                px: { xs: 1, md: 2 },
                // Padding counts inside the width, so the fields can't hang
                // past the panel's right edge
                boxSizing: 'border-box'
            }}
        >

            <Box sx={{ position: 'relative', mb: 5, textAlign: 'center', mt: 0.5 }}>
                <ToggleContainer>
                    <ToggleText
                        active={mode === 'borrow'}
                        glowColor="#00BFFF"
                        onClick={() => switchMode('borrow')}
                    >
                        BORROW
                    </ToggleText>
                    <ToggleText
                        active={mode === 'lend'}
                        glowColor="#00FF41"
                        onClick={() => switchMode('lend')}
                    >
                        LEND
                    </ToggleText>
                </ToggleContainer>

                <ToggleBar
                    onClick={handleToggleBarClick}
                    onKeyDown={handleToggleKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label="Toggle between borrow and lend modes"
                    aria-pressed={mode === 'lend'}
                >
                    <GlowingSlider
                        initial={false}
                        onClick={handleSliderClick}
                        tabIndex={-1}
                        aria-hidden="true"
                        glowColor={mode === 'borrow' ? 'rgba(0, 191, 255, 0.95)' : 'rgba(0, 255, 65, 0.95)'}
                        animate={{
                            x: mode === 'borrow' ? '0%' : '100%',
                            boxShadow: mode === 'borrow'
                                ? '0 0 10px rgba(0, 191, 255, 0.9)'
                                : '0 0 10px rgba(0, 255, 65, 0.9)'
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 200,
                            damping: 25,
                            mass: 1
                        }}
                        whileTap={{ scale: 0.9 }}
                    />
                </ToggleBar>
            </Box>

            <Box sx={{ maxWidth: '600px', mx: 'auto', overflow: 'hidden' }}>
                <form onSubmit={(e) => e.preventDefault()}>
                    {formStep === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <motion.div
                                animate={isClicked ? { x: '-100%', opacity: 0 } : { x: 0, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <RetroTextField
                                    type="email"
                                    placeholder="Email Address"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    onBlur={() => handleBlur('email', formData.email)}
                                    className={pulsedField === 'email' ? 'field-pulse' : ''}
                                    required
                                />
                            </motion.div>

                            <motion.div
                                animate={isClicked ? { x: '100%', opacity: 0 } : { x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <RetroTextField
                                    type="text"
                                    placeholder={`${mode === 'borrow' ? 'Loan' : 'Investment'} Amount`}
                                    value={displayAmount}
                                    onChange={handleAmountChange}
                                    onFocus={handleAmountFocus}
                                    onBlur={handleAmountBlur}
                                    className={pulsedField === 'amount' ? 'field-pulse' : ''}
                                    required
                                />
                            </motion.div>

                            <motion.div
                                animate={isClicked ? { x: '-100%', opacity: 0 } : { x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <RetroTextField
                                    type="text"
                                    placeholder={mode === 'borrow' ? 'Loan Purpose' : 'Investment Strategy'}
                                    value={formData.purpose}
                                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                                    onBlur={() => handleBlur('purpose', formData.purpose)}
                                    className={pulsedField === 'purpose' ? 'field-pulse' : ''}
                                    required
                                />
                            </motion.div>

                            <motion.div
                                animate={isClicked ? { x: '100%', opacity: 0 } : { x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                style={{ position: 'relative' }}
                            >
                                <RetroTextField
                                    type="text"
                                    placeholder="Country"
                                    value={formData.country}
                                    onChange={(e) => {
                                        setFormData({ ...formData, country: e.target.value });
                                        setShowCountrySuggestions(true);
                                    }}
                                    onFocus={() => setShowCountrySuggestions(true)}
                                    onBlur={() => {
                                        // Delay so a tap on a suggestion lands first
                                        setTimeout(() => setShowCountrySuggestions(false), 150);
                                        handleBlur('country', formData.country);
                                    }}
                                    autoComplete="off"
                                    className={pulsedField === 'country' ? 'field-pulse' : ''}
                                    required
                                />
                                {showCountrySuggestions && countrySuggestions.length > 0 && (
                                    <Box
                                        sx={{
                                            // In normal flow (not absolute): the form container
                                            // clips overflow for its slide animations, which cut
                                            // the list off after two entries. In flow it pushes
                                            // the button down and every match is reachable.
                                            margin: '-11px 0 15px',
                                            maxHeight: '250px',
                                            overflowY: 'auto',
                                            backgroundColor: '#1a237e',
                                            border: '2px solid #00BFFF',
                                            borderRadius: '4px',
                                            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.45)'
                                        }}
                                    >
                                        {countrySuggestions.map((name) => (
                                            <Box
                                                key={name}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    setFormData({ ...formData, country: name });
                                                    setShowCountrySuggestions(false);
                                                }}
                                                sx={{
                                                    padding: '10px 14px',
                                                    color: '#FFFFFF',
                                                    fontSize: '0.9rem',
                                                    cursor: 'pointer',
                                                    borderTop: '1px solid rgba(0, 191, 255, 0.25)',
                                                    '&:first-of-type': { borderTop: 'none' },
                                                    '&:hover': { backgroundColor: '#3949ab' }
                                                }}
                                            >
                                                {name}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </motion.div>
                        </Box>
                    )}

                    {formStep === 2 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <motion.div
                                initial={{ x: '100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <RetroTextField
                                    type="text"
                                    placeholder="Name"
                                    value={extendedFormData.name}
                                    onChange={(e) => setExtendedFormData({ ...extendedFormData, name: e.target.value })}
                                    onBlur={() => handleBlur('name', extendedFormData.name)}
                                    className={pulsedField === 'name' ? 'field-pulse' : ''}
                                    required
                                />
                            </motion.div>

                            <motion.div
                                initial={{ x: '-100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <RetroTextField
                                    type="text"
                                    placeholder="Company"
                                    value={extendedFormData.company}
                                    onChange={(e) => setExtendedFormData({ ...extendedFormData, company: e.target.value })}
                                    onBlur={() => handleBlur('company', extendedFormData.company)}
                                    className={pulsedField === 'company' ? 'field-pulse' : ''}
                                    required
                                />
                            </motion.div>

                            <motion.div
                                initial={{ x: '100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <RetroRadioGroup>
                                    <RetroFieldLabel>
                                        CRYPTO COLLATERAL
                                    </RetroFieldLabel>
                                    <RetroChoicesContainer>
                                        <RetroRadioButton
                                            aria-pressed={extendedFormData.cryptoCollateral === 'yes'}
                                            selected={extendedFormData.cryptoCollateral === 'yes'}
                                            onClick={() => handleRadioSelect('cryptoCollateral', 'yes')}
                                            type="button"
                                        >
                                            YES
                                        </RetroRadioButton>
                                        <RetroRadioButton
                                            aria-pressed={extendedFormData.cryptoCollateral === 'no'}
                                            selected={extendedFormData.cryptoCollateral === 'no'}
                                            onClick={() => handleRadioSelect('cryptoCollateral', 'no')}
                                            type="button"
                                        >
                                            NO
                                        </RetroRadioButton>
                                    </RetroChoicesContainer>
                                </RetroRadioGroup>
                            </motion.div>

                            {mode === 'lend' ? (
                                <motion.div
                                    initial={{ x: '-100%', opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                >
                                    <RetroTextField
                                        type="number"
                                        placeholder="Years of Lending Experience"
                                        value={extendedFormData.lendingExperience}
                                        onChange={(e) => setExtendedFormData({ ...extendedFormData, lendingExperience: e.target.value })}
                                        onBlur={() => handleBlur('lendingExperience', extendedFormData.lendingExperience)}
                                        className={pulsedField === 'lendingExperience' ? 'field-pulse' : ''}
                                        required
                                    />
                                </motion.div>
                            ) : (
                                <motion.div
                                    initial={{ x: '-100%', opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                >
                                    <RetroTextField
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="Annual Income"
                                        value={extendedFormData.annualIncome}
                                        onChange={(e) => setExtendedFormData({ ...extendedFormData, annualIncome: formatUsdInput(e.target.value).display })}
                                        onBlur={() => handleBlur('annualIncome', extendedFormData.annualIncome)}
                                        className={pulsedField === 'annualIncome' ? 'field-pulse' : ''}
                                        required
                                    />
                                </motion.div>
                            )}

                            <motion.div
                                initial={{ x: '100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                <RetroRadioGroup>
                                    <RetroFieldLabel>
                                        FIAT COLLATERAL
                                    </RetroFieldLabel>
                                    <RetroChoicesContainer>
                                        <RetroRadioButton
                                            aria-pressed={extendedFormData.fiatCollateral === 'yes'}
                                            selected={extendedFormData.fiatCollateral === 'yes'}
                                            onClick={() => handleRadioSelect('fiatCollateral', 'yes')}
                                            type="button"
                                        >
                                            YES
                                        </RetroRadioButton>
                                        <RetroRadioButton
                                            aria-pressed={extendedFormData.fiatCollateral === 'no'}
                                            selected={extendedFormData.fiatCollateral === 'no'}
                                            onClick={() => handleRadioSelect('fiatCollateral', 'no')}
                                            type="button"
                                        >
                                            NO
                                        </RetroRadioButton>
                                    </RetroChoicesContainer>
                                </RetroRadioGroup>
                            </motion.div>
                        </Box>
                    )}

                    {formStep === 3 && renderThirdStep()}

                    {/* Required-field notice for the current slide */}
                    {!isSubmitted && stepError && (
                        <Typography sx={{
                            fontFamily: "'Press Start 2P', cursive",
                            fontSize: '10px',
                            color: '#ff5252',
                            textAlign: 'center',
                            textShadow: '0 0 6px rgba(255, 82, 82, 0.8)',
                            mt: 1,
                            mb: 0.5
                        }}>
                            {stepError}
                        </Typography>
                    )}

                    {/* Hidden after submit — the animated-away button was
                        still occupying a tall empty band under the success
                        message */}
                    {!isSubmitted && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: formStep > 1 ? 'space-between' : 'center',
                        alignItems: 'center',
                        mt: 1.5
                    }}>
                        {/* Back sits bottom-left, mirroring the CONTINUE /
                            SUBMIT link bottom-right; entries stay in state,
                            so nothing typed is lost */}
                        {formStep > 1 && (
                            <motion.button
                                type="button"
                                aria-label="Back to the previous step"
                                onClick={() => {
                                    setStepError('');
                                    setFormStep(formStep - 1);
                                }}
                                whileTap={{ scale: 0.94 }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 10px',
                                    lineHeight: 1
                                }}
                            >
                                <RetroArrow glowColor={mode === 'lend' ? '#00FF41' : '#00BFFF'}>
                                    ◀
                                </RetroArrow>
                                <TextMotion
                                    style={{
                                        color: '#FFFFFF',
                                        fontFamily: "'Press Start 2P', cursive",
                                        fontSize: 'clamp(12px, 3.5vw, 18px)',
                                        textShadow: `0 0 6px #FFFFFF, 0 0 12px ${mode === 'lend' ? '#00FF41' : '#00BFFF'}`
                                    }}
                                >
                                    BACK
                                </TextMotion>
                            </motion.button>
                        )}
                        <SubmitButton
                            mode={mode}
                            isClicked={isClicked}
                            formStep={formStep}
                            onClick={() => {
                                const invalid = validateStep();
                                if (invalid) {
                                    showStepError(invalid);
                                    return;
                                }
                                setStepError('');
                                playRetroShot();
                                if (formStep === 1) {
                                    setIsClicked(true);
                                    setTimeout(() => {
                                        setFormStep(2);
                                        setIsClicked(false);
                                    }, 800);
                                } else if (formStep === 2) {
                                    setIsClicked(true);
                                    setTimeout(() => {
                                        setFormStep(3);
                                        setIsClicked(false);
                                    }, 800);
                                } else {
                                    handleFinalSubmit();
                                }
                            }}
                        />
                    </Box>
                    )}
                </form>
            </Box>
        </Box>
    );

};
export default LenderBorrowerApply;